/**
 * EvidenceGapCard - Card for displaying obligations with missing evidence
 * Used in Gaps & Risks page Evidence Gaps tab
 */
import React from 'react';
import { FileX, Upload, ArrowRight } from 'lucide-react';

export default function EvidenceGapCard({ gap, onSelect }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  // Extract missing evidence info
  const missingEvidence = gap.evidence_requirements?.required_evidence_types?.filter(
    (type, idx) => !gap.evidence_requirements?.evidence_ids?.[idx]
  ) || [];

  return (
    <div 
      className="bg-[#111111] border border-amber-500/30 rounded-xl p-5 hover:bg-[#151515] transition-all cursor-pointer"
      onClick={onSelect}
      data-testid={`evidence-gap-${gap.id || gap.obligation_id}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
          <FileX className="h-6 w-6 text-amber-400" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header badges */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="px-2 py-0.5 text-xs font-mono bg-[#1F1F1F] text-[#A0A0A0] rounded">
              {gap.obligation_code || 'N/A'}
            </span>
            <span className="px-2 py-0.5 text-xs font-bold bg-amber-500/20 text-amber-400 rounded">
              {gap.evidence_gaps_count || missingEvidence.length || 1} evidence gap{(gap.evidence_gaps_count || missingEvidence.length || 1) > 1 ? 's' : ''}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold text-white mb-3">
            {gap.obligation_title}
          </h3>

          {/* Missing Evidence List */}
          <div className="space-y-2 mb-4">
            <p className="text-sm font-medium text-[#A0A0A0]">Missing Evidence:</p>
            {(gap.missing_evidence || missingEvidence.map(type => ({ evidenceType: type, description: `${type} evidence required` }))).map((evidence, idx) => (
              <div key={idx} className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-amber-300">{evidence.description || evidence.evidenceType}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">
                  Type: {evidence.evidenceType || 'Document'}
                </p>
              </div>
            ))}
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-4 text-xs text-[#6B6B6B]">
            <span>Due: {formatDate(gap.due_date)}</span>
            {gap.framework_code && (
              <span className="px-2 py-0.5 bg-[#1F1F1F] rounded">
                {gap.framework_code.replace('_', '/')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#1F1F1F]">
        <button 
          className="flex-1 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to evidence upload
            window.location.href = `/evidence/upload?obligation=${gap.id || gap.obligation_id}`;
          }}
        >
          <Upload size={14} />
          Upload Evidence
        </button>
        <button 
          className="flex-1 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-xs font-medium rounded-lg transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        >
          View Obligation
        </button>
      </div>
    </div>
  );
}
